import { useState, useRef, useEffect } from 'react';
import {
  Type, Heading1, MousePointerClick, Image as ImageIcon, Minus,
  StretchVertical, Columns, Share2, FileText, Plus, Trash2,
  ArrowUp, ArrowDown, Copy,
} from 'lucide-react';
import type { Block, BlockType, TemplateTheme } from '@/types/newsletter';
import { DEFAULT_THEME } from '@/types/newsletter';
import { renderEmailHtml } from '@/lib/emailRenderer';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';

type Props = {
  blocks: Block[];
  theme: TemplateTheme;
  onBlocksChange: (blocks: Block[]) => void;
  onThemeChange: (theme: TemplateTheme) => void;
};

const BLOCK_DEFINITIONS: Array<{
  type: BlockType;
  label: string;
  icon: typeof Type;
  create: () => Block;
}> = [
  {
    type: 'heading', label: 'Heading', icon: Heading1,
    create: () => ({ id: crypto.randomUUID(), type: 'heading',
      content: { text: 'Your headline here', level: 2, align: 'left' } }),
  },
  {
    type: 'text', label: 'Paragraph', icon: Type,
    create: () => ({ id: crypto.randomUUID(), type: 'text',
      content: { text: 'Write your message here. Use merge tags like {{firstName}} to personalize.', align: 'left' } }),
  },
  {
    type: 'button', label: 'Button', icon: MousePointerClick,
    create: () => ({ id: crypto.randomUUID(), type: 'button',
      content: { text: 'Learn More', url: 'https://talbertusa.com', align: 'center' } }),
  },
  {
    type: 'image', label: 'Image', icon: ImageIcon,
    create: () => ({ id: crypto.randomUUID(), type: 'image',
      content: { url: '', alt: '', align: 'center' } }),
  },
  {
    type: 'divider', label: 'Divider', icon: Minus,
    create: () => ({ id: crypto.randomUUID(), type: 'divider', content: {} }),
  },
  {
    type: 'spacer', label: 'Spacer', icon: StretchVertical,
    create: () => ({ id: crypto.randomUUID(), type: 'spacer', content: { height: 32 } }),
  },
  {
    type: 'columns', label: 'Columns', icon: Columns,
    create: () => ({ id: crypto.randomUUID(), type: 'columns',
      content: { columns: [
        [{ id: crypto.randomUUID(), type: 'text', content: { text: 'Left column' } }],
        [{ id: crypto.randomUUID(), type: 'text', content: { text: 'Right column' } }],
      ] } }),
  },
  {
    type: 'social', label: 'Social', icon: Share2,
    create: () => ({ id: crypto.randomUUID(), type: 'social',
      content: { links: [
        { network: 'Website', url: 'https://talbertusa.com' },
        { network: 'LinkedIn', url: '' },
      ] } }),
  },
  {
    type: 'footer', label: 'Footer', icon: FileText,
    create: () => ({ id: crypto.randomUUID(), type: 'footer',
      content: {
        companyName: 'Talbert Architectural Panels & Doors, Inc.',
        address: 'City of Industry, CA',
        fscLine: 'FSC® C014235',
      } }),
  },
];

export function EmailEditor({ blocks, theme, onBlocksChange, onThemeChange }: Props) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [previewMode, setPreviewMode] = useState<'desktop' | 'mobile'>('desktop');

  const selectedBlock = blocks.find((b) => b.id === selectedId) ?? null;

  function addBlock(def: typeof BLOCK_DEFINITIONS[number]) {
    const newBlock = def.create();
    onBlocksChange([...blocks, newBlock]);
    setSelectedId(newBlock.id);
  }

  function updateBlock(id: string, patch: Partial<Block>) {
    onBlocksChange(blocks.map((b) => (b.id === id ? { ...b, ...patch } : b)));
  }

  function removeBlock(id: string) {
    onBlocksChange(blocks.filter((b) => b.id !== id));
    if (selectedId === id) setSelectedId(null);
  }

  function duplicateBlock(id: string) {
    const idx = blocks.findIndex((b) => b.id === id);
    if (idx < 0) return;
    const copy = { ...blocks[idx], id: crypto.randomUUID() };
    onBlocksChange([...blocks.slice(0, idx + 1), copy, ...blocks.slice(idx + 1)]);
  }

  function moveBlock(id: string, dir: -1 | 1) {
    const idx = blocks.findIndex((b) => b.id === id);
    if (idx < 0) return;
    const next = [...blocks];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    onBlocksChange(next);
  }

  return (
    <div className="grid grid-cols-12 gap-4 h-full min-h-[34rem]">
      {/* Left panel: block palette */}
      <aside className="col-span-2 border border-border rounded-sm bg-bg-subtle overflow-y-auto">
        <div className="px-3 py-2 kicker border-b border-border">Blocks</div>
        <div className="p-2 space-y-1">
          {BLOCK_DEFINITIONS.map((def) => {
            const Icon = def.icon;
            return (
              <button
                key={def.type}
                type="button"
                onClick={() => addBlock(def)}
                className="w-full flex items-center gap-2 px-2 py-2 rounded-sm border border-transparent
                           text-xs text-text-muted hover:text-text hover:bg-bg-hover hover:border-border"
              >
                <Icon size={14} className="text-text-dim" />
                {def.label}
              </button>
            );
          })}
        </div>
      </aside>

      {/* Center: canvas */}
      <div className="col-span-7 border border-border rounded-sm bg-bg-subtle overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-4 py-2 border-b border-border bg-bg-panel">
          <div className="kicker">Preview</div>
          <div className="flex gap-1">
            <button
              type="button"
              onClick={() => setPreviewMode('desktop')}
              className={`px-3 py-1 text-2xs uppercase tracking-ultra rounded-sm ${
                previewMode === 'desktop' ? 'bg-bg-hover text-text' : 'text-text-dim hover:text-text'
              }`}
            >
              Desktop
            </button>
            <button
              type="button"
              onClick={() => setPreviewMode('mobile')}
              className={`px-3 py-1 text-2xs uppercase tracking-ultra rounded-sm ${
                previewMode === 'mobile' ? 'bg-bg-hover text-text' : 'text-text-dim hover:text-text'
              }`}
            >
              Mobile
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6" style={{ background: theme.backgroundColor }}>
          <div
            className="mx-auto bg-white border border-border"
            style={{
              maxWidth: previewMode === 'mobile' ? '375px' : `${theme.containerWidth}px`,
              transition: 'max-width 200ms ease',
            }}
          >
            {blocks.length === 0 ? (
              <div className="py-16 text-center text-text-dim text-sm">
                Click a block in the left panel to get started.
              </div>
            ) : (
              blocks.map((block, idx) => (
                <BlockRenderer
                  key={block.id}
                  block={block}
                  theme={theme}
                  selected={block.id === selectedId}
                  onSelect={() => setSelectedId(block.id)}
                  onMoveUp={() => moveBlock(block.id, -1)}
                  onMoveDown={() => moveBlock(block.id, 1)}
                  onDuplicate={() => duplicateBlock(block.id)}
                  onRemove={() => removeBlock(block.id)}
                  canMoveUp={idx > 0}
                  canMoveDown={idx < blocks.length - 1}
                />
              ))
            )}
          </div>
        </div>
      </div>

      {/* Right panel: inspector */}
      <aside className="col-span-3 border border-border rounded-sm bg-bg-subtle overflow-y-auto">
        <div className="px-3 py-2 kicker border-b border-border">
          {selectedBlock ? `${selectedBlock.type} settings` : 'Design'}
        </div>
        <div className="p-3">
          {selectedBlock ? (
            <BlockInspector
              block={selectedBlock}
              onChange={(patch) => updateBlock(selectedBlock.id, patch)}
            />
          ) : (
            <ThemeInspector theme={theme} onChange={onThemeChange} />
          )}
        </div>
      </aside>
    </div>
  );
}

// ─────────── Block rendering with overlays ───────────

function BlockRenderer({
  block, theme, selected, onSelect,
  onMoveUp, onMoveDown, onDuplicate, onRemove, canMoveUp, canMoveDown,
}: {
  block: Block;
  theme: TemplateTheme;
  selected: boolean;
  onSelect: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onDuplicate: () => void;
  onRemove: () => void;
  canMoveUp: boolean;
  canMoveDown: boolean;
}) {
  // Render this block alone via email renderer, then inject into an iframe-ish container
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!ref.current) return;
    const html = renderEmailHtml([block], theme, { skipTracking: true });
    // Extract just the inner email table from the rendered HTML
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const container = doc.querySelector('table table');
    if (container) ref.current.innerHTML = container.outerHTML;
  }, [block, theme]);

  return (
    <div
      onClick={(e) => { e.stopPropagation(); onSelect(); }}
      className={`
        relative cursor-pointer transition-all
        ${selected ? 'outline outline-2 outline-brass-400' : 'hover:outline hover:outline-1 hover:outline-border-strong'}
      `}
    >
      <div ref={ref} />

      {selected && (
        <div className="absolute -top-8 right-0 flex gap-1 bg-bg-raised border border-border-strong rounded-sm shadow-raised p-0.5 z-10">
          <BlockAction onClick={(e) => { e.stopPropagation(); onMoveUp(); }} disabled={!canMoveUp}>
            <ArrowUp size={11} />
          </BlockAction>
          <BlockAction onClick={(e) => { e.stopPropagation(); onMoveDown(); }} disabled={!canMoveDown}>
            <ArrowDown size={11} />
          </BlockAction>
          <BlockAction onClick={(e) => { e.stopPropagation(); onDuplicate(); }}>
            <Copy size={11} />
          </BlockAction>
          <BlockAction danger onClick={(e) => { e.stopPropagation(); onRemove(); }}>
            <Trash2 size={11} />
          </BlockAction>
        </div>
      )}
    </div>
  );
}

function BlockAction({
  children, onClick, disabled, danger,
}: {
  children: React.ReactNode;
  onClick: (e: React.MouseEvent) => void;
  disabled?: boolean;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`
        p-1.5 rounded-sm transition-colors
        ${disabled
          ? 'text-text-dim opacity-30'
          : danger
            ? 'text-danger hover:bg-danger/10'
            : 'text-text-muted hover:text-text hover:bg-bg-hover'}
      `}
    >
      {children}
    </button>
  );
}

// ─────────── Block-specific inspector ───────────

function BlockInspector({ block, onChange }: { block: Block; onChange: (patch: Partial<Block>) => void }) {
  const c = block.content;

  function updateContent(patch: Record<string, unknown>) {
    onChange({ content: { ...c, ...patch } });
  }

  switch (block.type) {
    case 'heading':
      return (
        <div className="space-y-3">
          <Textarea label="Text" rows={2}
            value={String(c.text ?? '')}
            onChange={(e) => updateContent({ text: e.target.value })} />
          <Select label="Level" value={String(c.level ?? 2)}
            onChange={(e) => updateContent({ level: Number(e.target.value) })}
            options={[
              { value: '1', label: 'H1 — Large' },
              { value: '2', label: 'H2 — Medium' },
              { value: '3', label: 'H3 — Small' },
            ]}
          />
          <Select label="Align" value={String(c.align ?? 'left')}
            onChange={(e) => updateContent({ align: e.target.value })}
            options={[{ value: 'left', label: 'Left' }, { value: 'center', label: 'Center' }, { value: 'right', label: 'Right' }]}
          />
        </div>
      );

    case 'text':
      return (
        <div className="space-y-3">
          <Textarea label="Text" rows={6}
            value={String(c.text ?? c.html ?? '')}
            onChange={(e) => updateContent({ text: e.target.value, html: undefined })}
            hint="Use {{firstName}}, {{lastName}}, {{email}}, {{company}} for merge tags."
          />
          <Select label="Align" value={String(c.align ?? 'left')}
            onChange={(e) => updateContent({ align: e.target.value })}
            options={[{ value: 'left', label: 'Left' }, { value: 'center', label: 'Center' }, { value: 'right', label: 'Right' }]}
          />
        </div>
      );

    case 'button':
      return (
        <div className="space-y-3">
          <Input label="Button Text" value={String(c.text ?? '')} onChange={(e) => updateContent({ text: e.target.value })} />
          <Input label="URL" type="url" value={String(c.url ?? '')} onChange={(e) => updateContent({ url: e.target.value })} />
          <div className="grid grid-cols-2 gap-2">
            <Input label="Button Color" type="color"
              value={String(c.color ?? '#85572e')}
              onChange={(e) => updateContent({ color: e.target.value })}
            />
            <Input label="Text Color" type="color"
              value={String(c.textColor ?? '#ffffff')}
              onChange={(e) => updateContent({ textColor: e.target.value })}
            />
          </div>
          <Select label="Align" value={String(c.align ?? 'center')}
            onChange={(e) => updateContent({ align: e.target.value })}
            options={[{ value: 'left', label: 'Left' }, { value: 'center', label: 'Center' }, { value: 'right', label: 'Right' }]}
          />
        </div>
      );

    case 'image':
      return (
        <div className="space-y-3">
          <Input label="Image URL" type="url" value={String(c.url ?? '')}
            onChange={(e) => updateContent({ url: e.target.value })}
            hint="Publicly accessible URL. Host on your CDN or Talbert's site."
          />
          <Input label="Alt text" value={String(c.alt ?? '')}
            onChange={(e) => updateContent({ alt: e.target.value })} />
          <Input label="Click URL (optional)" type="url" value={String(c.link ?? '')}
            onChange={(e) => updateContent({ link: e.target.value })} />
          <Input label="Width" value={String(c.width ?? '100%')}
            onChange={(e) => updateContent({ width: e.target.value })}
            hint='e.g. "100%", "400px"'
          />
        </div>
      );

    case 'divider':
      return (
        <Input label="Color" type="color"
          value={String(c.color ?? '#e9e6df')}
          onChange={(e) => updateContent({ color: e.target.value })}
        />
      );

    case 'spacer':
      return (
        <Input label="Height (px)" type="number" min="0" step="4"
          value={String(c.height ?? 32)}
          onChange={(e) => updateContent({ height: Number(e.target.value) || 0 })}
        />
      );

    case 'social': {
      const links = (c.links as Array<{ network: string; url: string }>) ?? [];
      return (
        <div className="space-y-2">
          {links.map((l, i) => (
            <div key={i} className="flex gap-2">
              <Input placeholder="Network" value={l.network}
                onChange={(e) => {
                  const next = [...links]; next[i] = { ...l, network: e.target.value };
                  updateContent({ links: next });
                }}
              />
              <Input placeholder="URL" value={l.url}
                onChange={(e) => {
                  const next = [...links]; next[i] = { ...l, url: e.target.value };
                  updateContent({ links: next });
                }}
              />
              <button type="button"
                onClick={() => updateContent({ links: links.filter((_, x) => x !== i) })}
                className="p-2 text-text-dim hover:text-danger"
              ><Trash2 size={12} /></button>
            </div>
          ))}
          <Button size="sm" variant="secondary" leftIcon={<Plus size={12} />} type="button"
            onClick={() => updateContent({ links: [...links, { network: '', url: '' }] })}
          >Add Link</Button>
        </div>
      );
    }

    case 'footer':
      return (
        <div className="space-y-3">
          <Input label="Company Name" value={String(c.companyName ?? '')}
            onChange={(e) => updateContent({ companyName: e.target.value })} />
          <Input label="Address" value={String(c.address ?? '')}
            onChange={(e) => updateContent({ address: e.target.value })} />
          <Input label="Certification Line" value={String(c.fscLine ?? '')}
            onChange={(e) => updateContent({ fscLine: e.target.value })} />
          <Textarea label="Extra Text" rows={2}
            value={String(c.text ?? '')}
            onChange={(e) => updateContent({ text: e.target.value })} />
        </div>
      );

    default:
      return <div className="text-xs text-text-dim">No settings for this block type.</div>;
  }
}

function ThemeInspector({ theme, onChange }: { theme: TemplateTheme; onChange: (theme: TemplateTheme) => void }) {
  function update<K extends keyof TemplateTheme>(key: K, val: TemplateTheme[K]) {
    onChange({ ...theme, [key]: val });
  }
  return (
    <div className="space-y-3">
      <p className="text-2xs text-text-dim">
        Select a block to edit it, or adjust the global design below.
      </p>
      <div className="grid grid-cols-2 gap-2">
        <Input label="Primary" type="color"
          value={theme.primaryColor}
          onChange={(e) => update('primaryColor', e.target.value)} />
        <Input label="Background" type="color"
          value={theme.backgroundColor}
          onChange={(e) => update('backgroundColor', e.target.value)} />
        <Input label="Text" type="color"
          value={theme.textColor}
          onChange={(e) => update('textColor', e.target.value)} />
        <Input label="Width" type="number" min="480" max="800" step="20"
          value={theme.containerWidth}
          onChange={(e) => update('containerWidth', Number(e.target.value))} />
      </div>
      <div className="pt-3 border-t border-border">
        <div className="kicker mb-2">Reset</div>
        <Button size="sm" variant="ghost" type="button" onClick={() => onChange(DEFAULT_THEME)}>
          Restore Talbert defaults
        </Button>
      </div>
    </div>
  );
}
